import { yupResolver } from '@hookform/resolvers/yup';
// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import CustomTextMask from '@/components/ui/CustomTextMask';
import Loader from '@/components/ui/Loader';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type { ContactUsType } from '@/types/ContactUsType';

declare global {
  interface Window {
    grecaptcha: any;
    Tawk_API: {
      maximize: () => void;
      minimize: () => void;
      toggle: () => void;
      showWidget: () => void;
      hideWidget: () => void;
      popup: () => void;
      onLoad?: () => void;
    };
  }
}
// declare global {
//   interface Window {}
// }

const Contact = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [openSnack, setSnackOpen] = useState(false);

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const validationSchema = Yup.object().shape(
    {
      Name: Yup.string().required('Full Name is required'),
      Email: Yup.string()
        .required('Email Address is required')
        .email('Enter valid Email Address'),
      Phone: Yup.string()
        .nullable()
        .notRequired()
        .when('Phone', {
          is: (value: string) => value?.length,
          then: (rule) =>
            rule.matches(
              /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
              'Phone Number must in the format (000) 000-0000'
            ),
        }),
      Message: Yup.string().required('Message is required'),
    },
    [
      // Add Cyclic deps here because when require itself
      ['Phone', 'Phone'],
    ]
  );

  const { handleSubmit, control, reset } = useForm<ContactUsType>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      ContactUsType,
      any
    >,
    defaultValues: {
      Name: '',
      Email: '',
      Phone: '',
      Message: '',
    },
  });

  useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const onSubmit = async (data: ContactUsType) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: ContactUsType = data;
          postData.Token = token;

          const address = `/api/contactus/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData?.status === 'Success') {
            reset();
            setSnackOpen(true);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
  };

  const openChat = () => {
    window.Tawk_API?.maximize();
  };

  return (
    <AppLayout
      meta={
        <Meta
          title="Contact TaxZerone® | E-file IRS Federal Tax Forms Support"
          description="Contact TaxZerone for expert assistance with e-filing IRS federal tax forms. We're here to help you navigate tax season seamlessly."
          keywords="TaxZerone, file IRS form, authorized IRS e-file provider, IRS-approved tax preparers, IRS-approved online tax filing, IRS-approved tax preparers, e-file provider, authorized e file provider, irs approved online tax filing"
          canonical="contact-us/"
        />
      }
    >
      <Loader isLoading={isLoading} />
      <Container maxWidth="md">
        <Stack
          direction={{ xs: 'column', md: 'column' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0, textAlign: 'center' }, py: 3 }}
        >
          <Box
            component="div"
            sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                fontWeight: 600,
                color: '#374151',
                mb: 3,
              }}
            >
              <span className="highlight-container">
                <span className="highlight">Contact Us</span>
              </span>{' '}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
              }}
            >
              We&apos;re here to help with any questions or concerns you may
              have. Please choose any of the options below to let us know your
              questions and simplify your tax filings.
            </Typography>
          </Box>
        </Stack>
      </Container>

      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={0}
            mt={{ xs: 0, sm: 0, md: 3 }}
            pb={2}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 0,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Have questions or need assistance in{' '}
                <Hidden smDown>
                  <br />
                </Hidden>{' '}
                filing forms with the IRS?
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            m={0}
            pb={{ xs: 4, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                component="button"
                type="button"
                onClick={openChat}
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent',
                  p: 0,
                  m: 0,
                  textDecoration: 'none',
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: 5,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: 'transparent',
                    borderRadius: 10,
                    height: '100%',
                    boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                    transition: 'transform 0.5s ease',

                    '&:hover': {
                      boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(10px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#efefef',
                      borderRadius: '50%',
                      width: 90,
                      height: 90,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mx: 'auto',
                    }}
                  >
                    <picture style={{ width: 60, height: 60 }}>
                      <source srcSet="/assets/images/chat-icon.webp" />
                      <source srcSet="/assets/images/chat-icon.png" />
                      <img
                        src="/assets/images/chat-icon.png"
                        alt="Live chat support"
                        loading="lazy"
                        width={60}
                        height={60}
                      />
                    </picture>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                      fontWeight: 600,
                      my: 3,
                    }}
                  >
                    Live chat support
                  </Typography>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{ textAlign: 'center' }}
                  >
                    Our live chat support is available{' '}
                    <strong>Monday through Friday,</strong> from{' '}
                    <span style={{ fontWeight: 600 }}>
                      8:00 AM - 6:00 PM CST,
                    </span>{' '}
                    to assist you with any questions or issues in real time.
                  </Typography>
                </Paper>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/email-icon.webp" />
                    <source srcSet="/assets/images/email-icon.png" />
                    <img
                      src="/assets/images/email-icon.png"
                      alt="Email support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Email support
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ textAlign: 'center' }}
                >
                  Write to us your questions to{' '}
                  <Link
                    href="mailto:support@taxzerone.com"
                    target="_blank"
                    sx={{ color: '#0202FF' }}
                  >
                    <b>support@taxzerone.com</b>
                  </Link>
                  .<br />
                  Our support team is{' '}
                  <span style={{ fontWeight: 600 }}>available 24/7</span> and
                  will respond to your inquiry as soon as possible.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/support-icon.webp" />
                    <source srcSet="/assets/images/support-icon.png" />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="Customer Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Customer Support
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ textAlign: 'center' }}
                >
                  Contact our friendly customer support team at <br />
                  <Link href="tel:+14084447120" sx={{ color: '#0202ff' }}>
                    <b>(408) 444-7120</b>
                  </Link>
                  . We are available from{' '}
                  <span style={{ fontWeight: 600 }}>Monday to Friday,</span>{' '}
                  between{' '}
                  <span style={{ fontWeight: 600 }}>9 AM and 5 PM CST</span>
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 10,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/location-icon.webp" />
                    <source srcSet="/assets/images/location-icon.png" />
                    <img
                      src="/assets/images/location-icon.png"
                      alt="Our Address"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Our Address
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ textAlign: 'center' }}
                >
                  690 Saratoga Ave, Ste 100
                  <br /> San Jose, CA 95129
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ py: 2, my: 3 }}>
        <Grid
          container
          spacing={2}
          m={0}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
        >
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={openSnack}
            autoHideDuration={6000}
            onClose={handleSnackClose}
          >
            <Alert
              onClose={handleSnackClose}
              severity="success"
              sx={{
                width: '100%',
                backgroundColor: '#198754',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              Thanks for contacting us! We will be in touch with you shortly.
            </Alert>
          </Snackbar>

          <Grid size={{ xs: 12, sm: 9, md: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                fontWeight: 600,
                mb: 3,
                lineHeight: 1.5,
                textAlign: 'center',
              }}
            >
              Fill out the form below with your name, email, and a brief
              description of your issue. We will respond to your request
              promptly.
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                py: { xs: 2, sm: 5 },
                px: { xs: 1, sm: 5 },
                mb: 3,
                borderWidth: 2,
                borderColor: '#d4d6db',
                borderRadius: 5,
                boxShadow: '0 0 20px rgba(0,0,0,.12)',
              }}
            >
              <Grid
                container
                spacing={2}
                my={0}
                mx={0}
                className="MuiGrid-root-width"
                component="form"
                autoComplete="off"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid size={12} sx={{ pt: '0 !important' }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: 'red', fontStyle: 'italic' }}
                  >
                    Fields marked with (*) are mandatory
                  </Typography>
                </Grid>

                <Grid size={12} sx={{}}>
                  <Controller
                    name="Name"
                    control={control}
                    defaultValue=""
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        id="Name"
                        className={value ? 'prefilled' : 'mandatory'}
                        label="*Full Name"
                        variant="outlined"
                        color="secondary"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        sx={{ mb: 0 }}
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid size={12} sx={{}}>
                  <Controller
                    name="Email"
                    control={control}
                    defaultValue=""
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        id="Email"
                        className={value ? 'prefilled' : 'mandatory'}
                        label="*Email Address"
                        variant="outlined"
                        color="secondary"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        sx={{ mb: 0 }}
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid size={12} sx={{}}>
                  <Controller
                    name="Phone"
                    control={control}
                    defaultValue=""
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        id="Phone"
                        label="Phone Number"
                        variant="outlined"
                        color="secondary"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        InputProps={{
                          inputComponent: CustomTextMask as any,
                          inputProps: {
                            mask: '(#00) 000-0000',
                            maxLength: 14,
                          },
                        }}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid size={12} sx={{}}>
                  <Controller
                    name="Message"
                    control={control}
                    defaultValue=""
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        id="Message"
                        className={value ? 'prefilled' : 'mandatory'}
                        label="*Message"
                        multiline
                        rows={4}
                        color="secondary"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        sx={{ mb: 0 }}
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid size={12} sx={{ textAlign: 'center' }}>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    className="btn-primary medium"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      mb: 1,
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            lineHeight: 1.5,
            mb: 3,
          }}
        >
          Continue your IRS form filing or start
          <Hidden smDown>
            <br />
          </Hidden>{' '}
          afresh with TaxZerone
        </Typography>

        <Box component="div" sx={{ margin: 'auto', mb: 2 }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=contact`}
          >
            E-file Now
          </Button>
          <Typography variant="body2" sx={{ mt: 1, color: '#dfeae1' }}>
            Simple, quick, and secure e-filing
          </Typography>
        </Box>
      </Box>
    </AppLayout>
  );
};

export default Contact;
